import {
    Alert,
    Box,
    Button,
    Card,
    FormControl,
    FormHelperText,
    FormLabel,
    IconButton,
    Input,
    Stack,
    Typography,
} from "@mui/joy"
import React, {useCallback, useMemo, useState} from "react"
import {CheckCircle2, Eye, EyeOff, LockIcon, LucideMail, MailCheck} from "lucide-react"
import {Link, useSearchParams} from "react-router-dom"
import {motion} from "motion/react"
import {useTranslation} from "react-i18next"
import {useUITheme} from "../hooks/useUITheme"
import {useAsync} from "../hooks/useAsyncResult"
import {requestPasswordReset as _requestPasswordReset, resetPassword as _resetPassword} from "../services/api/user"

const MIN_PASSWORD_LENGTH = 8

/**
 * Both halves of the reset flow live here, chosen by the presence of a `token`
 * query parameter: without one the page asks for an email address, with one it
 * collects the new password. Keeping them in a single route means the link in
 * the email and the "forgot password" link from Login share a URL.
 */
const ResetPassword = () => {
    const uiTheme = useUITheme()
    const {t} = useTranslation()
    const [searchParams] = useSearchParams()
    const token = searchParams.get("token")

    const [email, setEmail] = useState("")
    const [password, setPassword] = useState("")
    const [confirmPassword, setConfirmPassword] = useState("")
    const [passwordVisible, setPasswordVisible] = useState(false)

    const requestResult = useAsync(_requestPasswordReset, false, true, false)
    const resetResult = useAsync(_resetPassword, false, true, false)

    const passwordTooShort = password.length > 0 && password.length < MIN_PASSWORD_LENGTH
    const passwordsMismatch = confirmPassword.length > 0 && password !== confirmPassword

    const canSubmitReset = useMemo(
        () => password.length >= MIN_PASSWORD_LENGTH && password === confirmPassword,
        [password, confirmPassword])

    const togglePasswordVisibility = useCallback(() => setPasswordVisible(v => !v), [])

    const handleRequestSubmit = (e: React.FormEvent) => {
        e.preventDefault()
        requestResult.execute(email.trim())
    }

    const handleResetSubmit = (e: React.FormEvent) => {
        e.preventDefault()
        resetResult.execute({token, password, confirmPassword})
    }

    const renderShell = (children: React.ReactNode) =>
        <Stack sx={{overflowX: "hidden"}}>
            <Stack paddingX={{xs: 2, sm: 3}} justifyContent={"center"} gap={2} alignItems={"center"} paddingY={3}
                   sx={{backgroundColor: "background.surface"}}>
                {/* The wrapper needs its own width: without it the card sizes to its content and
                    overflows narrow screens. */}
                <motion.div
                    style={{width: "100%", maxWidth: "35rem"}}
                    initial={{opacity: 0, y: 16}}
                    animate={{opacity: 1, y: 0}}
                    transition={{duration: 0.4}}>
                    <Card variant={"outlined"}
                          sx={{
                              paddingX: {xs: 2.5, sm: 5},
                              alignItems: "center",
                              gap: 1,
                              width: "100%",
                              boxSizing: "border-box",
                              paddingY: 4
                          }}>
                        <Box component={"img"}
                             width={"10rem"}
                             height={"auto"}
                             src={uiTheme.fullLogo}
                             alt={'LinguaScore Logo'}/>
                        {children}
                    </Card>
                </motion.div>
            </Stack>
        </Stack>

    // Password successfully changed
    if (resetResult.isSuccess)
        return renderShell(
            <Stack gap={2} alignItems="center" width="100%" paddingTop={1}>
                <CheckCircle2 size={48} color="var(--joy-palette-success-500)"/>
                <Typography level="h3" textAlign="center">{t("resetPasswordSuccessTitle")}</Typography>
                <Typography level="body-md" textColor="neutral.600" textAlign="center">
                    {t("resetPasswordSuccessDescription")}
                </Typography>
                <Link to="/login" style={{textDecoration: "none", width: "100%"}}>
                    <Button size="lg" fullWidth>{t("loginActionLabel")}</Button>
                </Link>
            </Stack>)

    // Reset link requested — the response is identical whether or not the
    // address is registered, so the copy must not imply an account was found.
    if (requestResult.isSuccess)
        return renderShell(
            <Stack gap={2} alignItems="center" width="100%" paddingTop={1}>
                <MailCheck size={48} color="var(--joy-palette-primary-500)"/>
                <Typography level="h3" textAlign="center">{t("resetPasswordEmailSentTitle")}</Typography>
                <Typography level="body-md" textColor="neutral.600" textAlign="center">
                    {t("resetPasswordEmailSentDescription", {email})}
                </Typography>
                <Link to="/login" style={{textDecoration: "none", width: "100%"}}>
                    <Button variant="outlined" size="lg" fullWidth>{t("backToLoginActionLabel")}</Button>
                </Link>
            </Stack>)

    // Step 2 — a token is present, collect the new password
    if (token)
        return renderShell(
            <>
                <Stack gap={1} alignItems="center" width="100%">
                    <Typography level="h2" textAlign="center">{t("resetPasswordTitle")}</Typography>
                    <Typography level="body-md" textColor="neutral.600" paddingX={{xs: 0, sm: "10%"}} textAlign="center">
                        {t("resetPasswordDescription")}
                    </Typography>
                </Stack>

                <Box component="form" onSubmit={handleResetSubmit} sx={{width: "100%", maxWidth: "22rem"}}>
                    <Stack gap={2} paddingY={1}>
                        <FormControl error={passwordTooShort}>
                            <FormLabel>{t("newPasswordFieldLabel")}</FormLabel>
                            <Input
                                size="lg"
                                startDecorator={<LockIcon size={20}/>}
                                endDecorator={<IconButton
                                    type="button" variant="plain" color="neutral"
                                    aria-label={passwordVisible ? "Hide password" : "Show password"}
                                    onClick={togglePasswordVisibility}>
                                    {passwordVisible ? <EyeOff size={20}/> : <Eye size={20}/>}
                                </IconButton>}
                                placeholder={t("newPasswordFieldPlaceholder")}
                                type={passwordVisible ? "text" : "password"}
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                sx={{"--Input-focusedThickness": "2px"}}/>
                            {passwordTooShort &&
                                <FormHelperText>{t("passwordTooShortMessage", {count: MIN_PASSWORD_LENGTH})}</FormHelperText>}
                        </FormControl>

                        <FormControl error={passwordsMismatch}>
                            <FormLabel>{t("confirmPasswordFieldLabel")}</FormLabel>
                            <Input
                                size="lg"
                                startDecorator={<LockIcon size={20}/>}
                                placeholder={t("confirmPasswordFieldPlaceholder")}
                                type={passwordVisible ? "text" : "password"}
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                sx={{"--Input-focusedThickness": "2px"}}/>
                            {passwordsMismatch &&
                                <FormHelperText>{t("FIELD_PASSWORD_NOT_MATCHING")}</FormHelperText>}
                        </FormControl>

                        <Button
                            type="submit"
                            size="lg"
                            fullWidth
                            loading={resetResult.loading}
                            disabled={!canSubmitReset}>
                            {t("resetPasswordActionLabel")}
                        </Button>
                    </Stack>
                </Box>
            </>)

    // Step 1 — no token, ask where to send the link
    return renderShell(
        <>
            <Stack gap={1} alignItems="center" width="100%">
                <Typography level="h2" textAlign="center">{t("forgotPasswordTitle")}</Typography>
                <Typography level="body-md" textColor="neutral.600" paddingX={{xs: 0, sm: "10%"}} textAlign="center">
                    {t("forgotPasswordDescription")}
                </Typography>
            </Stack>

            <Box component="form" onSubmit={handleRequestSubmit} sx={{width: "100%", maxWidth: "22rem"}}>
                <Stack gap={2} paddingY={1}>
                    <FormControl>
                        <FormLabel>{t("emailFieldLabel")}</FormLabel>
                        <Input
                            size="lg"
                            startDecorator={<LucideMail size={20}/>}
                            placeholder={t("emailFieldPlaceholder")}
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            sx={{"--Input-focusedThickness": "2px"}}/>
                    </FormControl>

                    <Button
                        type="submit"
                        size="lg"
                        fullWidth
                        loading={requestResult.loading}
                        disabled={!email.trim()}>
                        {t("sendResetLinkActionLabel")}
                    </Button>
                </Stack>
            </Box>

            <Typography level="body-sm" textAlign="center" sx={{marginTop: 2}}>
                <Link to="/login" style={{textDecoration: "none"}}>
                    <Typography component="span" level="title-sm" sx={{color: "primary.500", fontWeight: 600}}>
                        {t("backToLoginActionLabel")}
                    </Typography>
                </Link>
            </Typography>
        </>)
}

export default ResetPassword
